package com.siriusxm.pia.components

import androidx.compose.runtime.*
import com.siriusxm.cmc.Notification
import com.siriusxm.cmc.NotificationTypes
import com.siriusxm.pia.SXMUI
import kotlinx.datetime.*
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.max
import org.jetbrains.compose.web.attributes.min
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Instant

object DialogStyles : StyleSheet() {
    val dialog by style {
        maxWidth(800.px)

        className(NotificationStyles.box).style {
            marginBottom(1.em)
        }
    }
    val field by style {
        marginBottom(20.px)
        type("label").style {
            fontWeight("bold")
            marginLeft(5.px)
        }
        type("input[type=text]").style {
            width(100.percent)
        }
        type("input[type=password]").style {
            width(100.percent)
        }
        type("textarea").style {
            width(100.percent)
        }
    }

    val checkbox by style {
        display(DisplayStyle.Flex)
        lineHeight((1.7).cssRem)
        type("label").style {
            fontWeight("normal")
            marginLeft((0.5).cssRem)
        }
        type("input").style {
//            marginTop((0.33).cssRem)
        }
    }

    val fieldInstruction by style {
        fontSize((0.8).cssRem)
        marginTop((0.5).cssRem)
        color(SXMUI.subtleTextColor.value())
    }

    val fieldValidationError by style {
        fontSize((0.8).cssRem)
        marginTop((0.5).cssRem)
        color(SXMUI.messageAlertBorder.value())
    }

    val fieldContent by style {
        paddingTop((0.5).cssRem)
    }

    val textValue by style {
        padding((0.4).cssRem, (0.6).cssRem)
        backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
        borderRadius(4.px)
        fontSize(1.cssRem)
    }

    val radioGroupOption by style {
        display(DisplayStyle.Flex)
        marginBottom((0.4).cssRem)
        gap((0.4).cssRem)
    }

    val radioGroupOptionDescription by style {
        fontSize((0.8).cssRem)
        marginTop(3.px)
        color(SXMUI.subtleTextColor.value())
    }

    val buttonRadioGroup by style {
        display(DisplayStyle.InlineBlock)
    }

    val buttonRadioGroupButtons by style {
        display(DisplayStyle.Flex)
        shadowBorder()
        gap(1.px)
        backgroundColor(SXMUI.defaultDivider.value())
        borderRadius(SXMUI.boxRadius.value())
        overflow("hidden")
    }

    val buttonRadioGroupButton by style {
        backgroundColor(SXMUI.buttonBackgroundNormal.value())
        color(SXMUI.buttonForegroundNormal.value())
        fontWeight("bold")
        padding((0.4).cssRem, (1.5).cssRem, (0.2).cssRem)
        cursor("pointer")
        fontSize((0.9).cssRem)
    }
    val buttonRadioGroupSelected by style {
        backgroundColor(SXMUI.buttonBackgroundPrimary.value())
        color(SXMUI.buttonForegroundPrimary.value())
    }
    val scrollableCheckBoxSelector by style {
        backgroundColor(SXMUI.selectionControlSurface.value())
        overflow("hidden")
    }
    val scrollableCheckBoxSelectorContent by style {
        maxHeight(200.px)
        overflowY("auto")
        padding(4.px, 8.px)
    }
}

interface ErrorHandler {
    fun clearAll()
    fun error(message: String)
}

class DialogViewConfiguration(
    val errors: ErrorHandler
) : EntityViewConfiguration()


internal class Error(
    val message: String
)

/**
 * Dialog view that is displayed within a box with no padding.
 */
@Composable
fun BoxDialogView(builder: DialogViewConfiguration.() -> Unit) {
    Div({
        style {
            padding(10.px)
            backgroundColor(SXMUI.containerContentBackgroundColor.value())
        }
    }) {
        dialogView(null, builder)
    }
}

/**
 * Define a dialog view.
 */
@Composable
fun DialogView(builder: DialogViewConfiguration.() -> Unit, content: @Composable () -> Unit) {
    @Suppress("DEPRECATION")
    dialogView(null) {
        builder()
        content(content)
    }
}

@Deprecated("Use DialogView", ReplaceWith("DialogView"))
@Composable
fun dialogView(title: String? = null, builder: DialogViewConfiguration.() -> Unit) {
    var errors by remember { mutableStateOf(emptyList<Error>()) }

    val errorHandler = object : ErrorHandler {
        override fun error(message: String) {
            println("adding error: $message")
            errors = errors + Error(message)
        }

        override fun clearAll() {
            errors = emptyList()
        }
    }

    val config = DialogViewConfiguration(errorHandler).apply {
        this.title = title
        type = EntityViewType.DIALOG
        builder()
    }

    Div({
        classes(DialogStyles.dialog)
    }) {
        val content = config.content
        config.content {
            if (errors.isNotEmpty()) {
                errors.forEach {
                    NotificationBox(Notification(NotificationTypes.Error, it.message, null)) {
                        errors = errors - it
                    }
                }
            }
            content?.invoke()
        }
        entityView(config)
    }
}

interface DialogFieldConfig {
    var validationError: String?
    var instruction: String?
}

@Composable
fun dialogField(
    title: String?, instruction: String? = null, config: DialogFieldConfig.() -> Unit = {},
    content: @Composable () -> Unit
) {
    DialogField(title, instruction, config, content)
}

/**
 * Display a dialog field, with optional instructions and a validation error.
 */
@Composable
fun DialogField(
    title: String?, instruction: String? = null, config: DialogFieldConfig.() -> Unit = {},
    content: @Composable () -> Unit
) {
    val config = (object : DialogFieldConfig {
        override var validationError: String? = null
        override var instruction: String? = null
    }).apply {
        this.instruction = instruction
        config()
    }

    Div({ classes(DialogStyles.field) }) {
        if (title != null) {
            Label {
                Text(title)
            }
        }
        Div({ classes(DialogStyles.fieldContent) }) {
            content()
        }
        config.instruction?.let {
            Div({ classes(DialogStyles.fieldInstruction) }) {
                Text(it)
            }
        }
        config.validationError?.let { error ->
            Div({ classes(DialogStyles.fieldValidationError) }) {
                Text(error)
            }
        }
    }
}

/**
 * Displays a read-only text value within a dialog form, styled to visually align with
 * input fields. Use this instead of dialogField + Text when showing non-editable values
 * that should match the layout rhythm of adjacent form inputs.
 */
@Composable
fun dialogText(title: String, value: String, instruction: String? = null) {
    dialogField(title, instruction) {
        Div({ classes(DialogStyles.textValue) }) {
            Text(value)
        }
    }
}

@Composable
fun simpleTextField(value: String?, enabled: Boolean = true, update: (String?) -> Unit) {
    Input(InputType.Text) {
        value(value.orEmpty())
        if (!enabled) {
            disabled()
        }
        onInput {
            val newValue = it.value.ifBlank { null }
            update(newValue)
        }
    }
}

/**
 * A simple password input field that masks entered text. Follows the same pattern
 * as [simpleTextField] but renders as a password input using [InputType.Password].
 *
 * @param value The current value of the password field
 * @param enabled Whether the field is interactive
 * @param update Callback invoked when the field value changes, null if blank
 */
@Composable
fun simplePasswordField(value: String?, enabled: Boolean = true, update: (String?) -> Unit) {
    Input(InputType.Password) {
        value(value.orEmpty())
        if (!enabled) {
            disabled()
        }
        onInput {
            val newValue = it.value.ifBlank { null }
            update(newValue)
        }
    }
}

/**
 * A range where either end of the range can be null.
 */
data class OpenEndedRange<T : Comparable<T>>(val start: T?, val end: T?) {
    operator fun contains(value: T): Boolean {
        val aboveStart = start?.let { value >= it } ?: true
        val belowEnd = end?.let { value <= it } ?: true
        return aboveStart && belowEnd
    }
}


/**
 * A date/time selector
 * @param limit the limits of the allowed selection.
 */
@Composable
fun dateTimePicker(
    value: LocalDateTime?,
    limit: OpenEndedRange<LocalDateTime>? = null,
    update: (LocalDateTime?) -> Unit
) {
    Input(InputType.DateTimeLocal) {
        value(value?.toString().orEmpty())
        if (limit?.start != null) {
            min(limit.start.toString())
        }
        if (limit?.end != null) {
            max(limit.end.toString())
        }
        onInput {
            val raw = it.value.ifBlank { null }
            // While a year is being typed the browser can briefly report an out-of-range value
            // (e.g. "12026-03-01") that kotlinx-datetime rejects. Ignore it rather than crash;
            // the next keystroke delivers a valid value (UCA-1168).
            val newValue = raw?.let { runCatching { LocalDateTime.parse(it) }.getOrNull() ?: return@onInput }
            update(newValue)
        }
    }
}

/**
 * A date/time selector
 * @param limit the limits of the allowed selection.
 */
@Composable
fun datePicker(
    value: LocalDate?,
    limit: OpenEndedRange<LocalDate>? = null,
    update: (LocalDate?) -> Unit
) {
    Input(InputType.Date) {
        value(value?.toString().orEmpty())
        if (limit?.start != null) {
            min(limit.start.toString())
        }
        if (limit?.end != null) {
            max(limit.end.toString())
        }
        onInput {
            val raw = it.value.ifBlank { null }
            // While a year is being typed the browser can briefly report an out-of-range value
            // (e.g. "12026-03-01") that kotlinx-datetime rejects. Ignore it rather than crash;
            // the next keystroke delivers a valid value (UCA-1168).
            val newValue = raw?.let { runCatching { LocalDate.parse(it) }.getOrNull() ?: return@onInput }
            update(newValue)
        }
    }
}

/**
 * A date/time selector that takes and returns instants. uses the system
 * timezone, so the picker is shown in the user's local time.
 */
@Composable
fun instantPicker(
    value: Instant?, tz: TimeZone = TimeZone.currentSystemDefault(),
    limit: OpenEndedRange<Instant>? = null,
    update: (Instant?) -> Unit
) {
    dateTimePicker(
        value?.toLocalDateTime(tz),
        limit?.let {
            OpenEndedRange(it.start?.toLocalDateTime(tz), it.end?.toLocalDateTime(tz))
        }) {
        update(it?.toInstant(tz))
    }
}


@Composable
fun simpleCheckBox(label: String, checked: Boolean, enabled: Boolean = true, update: (Boolean) -> Unit) {
    Div({
        classes(DialogStyles.checkbox)
        style { cursor("pointer") }
        onClick { update(!checked) }
    }) {
        Input(InputType.Checkbox) {
            this.checked(checked)
            this.onInput {
                update(it.value)
            }
            if (!enabled) {
                this.disabled()
            }
        }

        Label {
            Text(label)
        }
    }
}

@Composable
fun <T> checkBoxSelector(
    items: List<T>,
    selectedItems: Set<String>,
    itemLabel: (T) -> String,
    itemIdentifier: (T) -> String,
    onSelectionChange: (Set<String>) -> Unit
) {
    items.forEach { item ->
        simpleCheckBox(
            itemLabel(item),
            selectedItems.contains(itemIdentifier(item))
        ) { selected ->
            val newSelectedItems =
                if (selected) {
                    selectedItems + itemIdentifier(item)
                } else {
                    selectedItems - itemIdentifier(item)
                }
            onSelectionChange(newSelectedItems)
        }
    }
}

@Composable
fun <T> scrollableCheckBoxSelector(
    items: List<T>,
    selectedItems: Set<String>,
    itemLabel: (T) -> String,
    itemIdentifier: (T) -> String,
    onSelectionChange: (Set<String>) -> Unit
) {
    Div(
        attrs = {
            classes(DialogStyles.scrollableCheckBoxSelector)
        }
    ) {
        Div({
            classes(DialogStyles.scrollableCheckBoxSelectorContent)
        }) {
            checkBoxSelector(
                items = items,
                selectedItems = selectedItems,
                itemLabel = itemLabel,
                itemIdentifier = itemIdentifier,
                onSelectionChange = onSelectionChange
            )
        }
    }
}

interface Validation {
    suspend fun assertField(check: Boolean, message: String)
    suspend fun assertField(check: suspend () -> Boolean, message: String)
    suspend fun orElse(block: suspend () -> Unit)

    val failed: Boolean
    val success: Boolean
}

suspend fun DialogViewConfiguration.validate(
    checkAll: Boolean = false,
    block: suspend Validation.() -> Unit
): Validation {
    var failed = false

    val validation = object : Validation {
        override val failed: Boolean get() = failed
        override val success: Boolean get() = !failed

        override suspend fun assertField(check: Boolean, message: String) {
            assertField({ check }, message)
        }

        override suspend fun assertField(check: suspend () -> Boolean, message: String) {
            if (!failed || checkAll) {
                if (!check()) {
                    failed = true
                    errors.error(message)
                }
            }
        }

        override suspend fun orElse(block: suspend () -> Unit) {
            if (!failed) {
                block()
            }
        }
    }
    validation.block()

    return validation
}